import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { from, map, of, switchMap } from 'rxjs';
import { debugMaxEntries } from 'src/app/consts';
import { randomString } from 'src/app/lib/random-string';
import { AppConfigService } from 'src/app/providers/config.service';
import { BrowserService } from 'src/app/services/browser.service';
import { StateService } from 'src/app/services/state.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private config: AppConfigService,
    private state: StateService,
    @Inject(PLATFORM_ID) private platformId: string,
    private browserService: BrowserService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    return this.addAuth(req).pipe(
      switchMap(_req => this.addRandomParam(_req)),
      switchMap(_req => this.addDebug(_req)),
      switchMap(_req => next.handle(_req))
    );
  }

  addDebug(req: HttpRequest<unknown>) {
    if (this.state.recordDebug) {
      if (this.state.debugResponseCache.length > debugMaxEntries) this.state.debugResponseCache.shift();
      this.state.debugResponseCache.push({ ...req, __time: Date.now(), __type: 'request' });
    }
    return of(req);
  }

  addRandomParam(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    if (!apiRequest) return of(req);
    return of(req.clone({ url: req.url + (req.url.includes('?') ? '&' : '?') + 'r=' + randomString(5) }));
  }

  addAuth(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    const guestAuthRequest = req.url.includes('/auth/guest');
    if (!apiRequest) return of(req);
    if (guestAuthRequest) return of(req);

    const headers: { [name: string]: string | string[] } = {};

    if (this.config.tenantPathFromDomain) {
      headers['picflow-tenant-path'] = this.config.tenantPathFromDomain;
    }

    // if (isPlatformServer(this.platformId)) {
    const domain =
      this.browserService.getTenantCustomDomain() || this.browserService.getCorrectPicflowUrl().split('//')[1];
    headers['x-ssr-origin'] = 'https://' + domain;
    // }

    let user = this.state.tokenData;
    const guest = !!this.state.guestAuthToken;
    const custom = this.state.customAuthToken;

    if (guest && user && req.url.includes('force-guest')) user = undefined;

    if (user) {
      return from(this.handleUser(headers)).pipe(map(() => req.clone({ setHeaders: headers })));
    } else if (guest) {
      return from(this.handleGuest(headers)).pipe(map(() => req.clone({ setHeaders: headers })));
    } else if (custom) {
      return from(this.handleCustom(headers)).pipe(map(() => req.clone({ setHeaders: headers })));
    }

    const newReq = req.clone({ setHeaders: headers });
    return of(newReq);
  }

  async handleGuest(headers: { [name: string]: string | string[] }) {
    await this.state.renewGuestAuthToken();
    if (!this.state.guestAuthToken) {
      console.warn('guest token was removed during renewGuestAuthToken');
      return; // if token was removed during renewGuestAuthToken
    }
    headers['authorization'] = `Bearer ${this.state.guestAuthToken}`;
  }

  async handleUser(headers: { [name: string]: string | string[] }) {
    await this.state.renewAuthToken();
    if (!this.state.tokenData) {
      console.warn('user token was removed during renewAuthToken');
      return;
    }
    headers['Authorization'] = `Bearer ${this.state.tokenData.idToken}`;
    if (this.state.activeTenant) headers['picflow-tenant'] = this.state.activeTenant.id;
    else if (this.state.visitingTenantId) headers['picflow-tenant'] = this.state.visitingTenantId;
    else if (this.state.tenants?.length) headers['picflow-tenant'] = this.state.tenants[0].id;
  }

  async handleCustom(headers: { [name: string]: string | string[] }) {
    headers['Authorization'] = `Bearer ${this.state.customAuthToken}`;
  }
}
